import React, { useState } from 'react';
import { useCurrentSystem, useScenes, useSchedules } from '../context/SystemContext';
import ScheduleCard from '../ScheduleCard';
import ScheduleEditor from '../ScheduleEditor';
import Calendar from '../Calendar';
import SystemPageHeader from '../components/SystemPageHeader';
import { Plus, Clock } from 'lucide-react';
import { doc, deleteDoc } from 'firebase/firestore';
import { firestore } from '../common/firebase';

function ScheduleView() {
	const { currentSystem } = useCurrentSystem();
	const { scenes = [] } = useScenes(currentSystem?.uid);
	const { schedules = [] } = useSchedules(currentSystem?.uid, 50);
	const [editingSchedule, setEditingSchedule] = useState(null);
	const [calendarSelectedDate, setCalendarSelectedDate] = useState(() => new Date());

	if (!currentSystem) {
		return (
			<div className="p-6 text-center text-gray-500 dark:text-gray-400">
				Select a system from the Systems menu to manage schedules.
			</div>
		);
	}

	const doDeleteSchedule = async (schedule) => {
		if (schedule?.uid) await deleteDoc(doc(firestore, 'schedules', schedule.uid));
	};

	return (
		<div className="p-4 sm:p-6 max-w-7xl mx-auto">
			<SystemPageHeader currentSystem={currentSystem} title="Schedule" subtitle="Calendar and one-time or repeating schedules" />

			<div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 sm:p-5 shadow-sm">
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
					<h2 className="font-semibold text-lg flex items-center gap-2 text-gray-900 dark:text-gray-100">
						<Clock size={18} className="text-[#880e4f]" /> Schedules
					</h2>
					<button
						onClick={() => {
							const d = new Date(calendarSelectedDate);
							d.setHours(d.getHours(), d.getMinutes(), 0, 0);
							setEditingSchedule({ name: 'New Schedule', defaultPerformAt: d });
						}}
						className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
					>
						<Plus size={15} /> Add
					</button>
				</div>

				<div className="mb-4">
					<Calendar
						schedules={schedules}
						scenes={scenes}
						currentSystem={currentSystem}
						onEditSchedule={setEditingSchedule}
						onSelectedDateChange={setCalendarSelectedDate}
					/>
				</div>

				<div className="space-y-2">
					{schedules.length > 0 ? schedules.map((sch) => (
						<ScheduleCard
							key={sch.uid}
							schedule={sch}
							editSchedule={setEditingSchedule}
							deleteSchedule={doDeleteSchedule}
							scenes={scenes}
						/>
					)) : (
						<div className="text-sm text-gray-500 dark:text-gray-400 italic p-4 border border-dashed rounded-xl">No schedules yet.</div>
					)}
				</div>
			</div>

			{editingSchedule && (
				<ScheduleEditor
					open={!!editingSchedule}
					schedule={editingSchedule}
					scenes={scenes}
					doneEdit={() => setEditingSchedule(null)}
				/>
			)}
		</div>
	);
}

export default ScheduleView;
